import { createAction } from 'redux-act'

import { Thunk } from '../types'
import {
    INITIALIZE_FILTERS,
    SET_FILTERS_CONFIGURATION,
    COMMIT_FILTERS_CONFIGURATION,
    REVERT_FILTERS_CONFIGURATION,
    SET_FILTERS_ERROR,
    SET_LOADING,
    SET_BLOCKING,
    SET_LAST_DATA_PATH_ENCODED,
    SET_SEARCH_REQUEST_ID,
    CLEAR_SEARCH_REQUEST_ID,
    PUSH_MODAL,
    POP_MODAL,
} from './action-types'
import { InitializeFiltersPayload, FilterClickPayload, SmartSearchResult } from './types'
import { FilterModalType } from '../../components/app-components/filter-sort-section/types'
import { getRelativeURL, decodeFromBase64 } from '../../utils/encoding-utils'
import { searchClient } from '../../clients'
import FiltersConfigurationTransport from '../../transports/filters-configuration-transport'
import { RequestUserConfig } from '../../common/types'
import { getCurrentOpenedFilterModal } from './selectors'

// ============================================================================
// Sync Actions
// ============================================================================

export const initializeFilters = createAction<InitializeFiltersPayload>(INITIALIZE_FILTERS)
export const setFiltersConfiguration = createAction<FilterClickPayload>(SET_FILTERS_CONFIGURATION)
export const commitFiltersConfiguration = createAction(COMMIT_FILTERS_CONFIGURATION)
export const revertFiltersConfiguration = createAction(REVERT_FILTERS_CONFIGURATION)
export const setFiltersError = createAction<Error | null>(SET_FILTERS_ERROR)
export const setLoading = createAction<boolean>(SET_LOADING)
export const setBlocking = createAction<boolean>(SET_BLOCKING)
export const setLastDataPathEncoded = createAction<string>(SET_LAST_DATA_PATH_ENCODED)
export const setSearchRequestId = createAction<string>(SET_SEARCH_REQUEST_ID)
export const clearSearchRequestId = createAction(CLEAR_SEARCH_REQUEST_ID)
export const pushModal = createAction<FilterModalType>(PUSH_MODAL)
export const popModal = createAction(POP_MODAL)

// ============================================================================
// API Helper
// ============================================================================

const _fetchFiltersConfiguration = async (
    user: RequestUserConfig,
    filter: string,
    clear?: string,
): Promise<FiltersConfigurationTransport> => {
    const response = await searchClient.getFiltersConfiguration({
        user,
        filter,
        clear,
    })

    return response.data!
}

// ============================================================================
// Thunks
// ============================================================================

/**
 * Handles a filter click: calls the filter API and updates state.
 */
export const filterClick =
    (encodedLink: string, shouldCommit: boolean, user: RequestUserConfig): Thunk<Promise<void>> =>
    async (dispatch, getState) => {
        dispatch(setLoading(true))
        try {
            const config = await _fetchFiltersConfiguration(user, getRelativeURL(encodedLink))
            dispatch(setFiltersConfiguration({ config, shouldCommit }))

            // Only update the URL state when the mobile filter modal is NOT open.
            // When the modal is open, URL updates are deferred until the submit button is clicked.
            const currentOpenedFilterModal = getCurrentOpenedFilterModal(getState())

            if (currentOpenedFilterModal !== FilterModalType.FILTERS) {
                const relativePath = getRelativeURL(config.encodedLink)
                dispatch(clearSearchRequestId())
                dispatch(setLastDataPathEncoded(relativePath))

                // When spaMode is false (e.g. map page), do a full page reload
                if (!getState().filters.spaMode) {
                    globalThis.location.href = decodeFromBase64(relativePath)
                }
            }
        } catch (error) {
            console.error({ message: 'Error occurred in filters configuration API', error })
            dispatch(setFiltersError(error as Error))
        } finally {
            dispatch(setLoading(false))
        }
    }

/**
 * Handles filter clear: calls the filter API with a clear parameter and commits.
 */
export const filterClear =
    (user: RequestUserConfig): Thunk<Promise<void>> =>
    async (dispatch, getState) => {
        const { filters } = getState()
        const filtersConfig = filters.filtersConfigurationResponse

        if (!filtersConfig) {
            return
        }

        dispatch(setLoading(true))
        try {
            const config = await _fetchFiltersConfiguration(user, getRelativeURL(filtersConfig.clearLink))

            dispatch(setFiltersConfiguration({ config, shouldCommit: true }))
            const relativePath = getRelativeURL(config.encodedLink)
            dispatch(setLastDataPathEncoded(relativePath))

            if (!getState().filters.spaMode) {
                globalThis.location.href = decodeFromBase64(relativePath)
            }
        } catch (error) {
            console.error({ message: 'Error occurred in filters configuration API', error })
            dispatch(setFiltersError(error as Error))
        } finally {
            dispatch(setLoading(false))
        }
    }

/**
 * Handles filter submit: either navigates (map) or pushes state (list).
 */
export const filterSubmit =
    (encodedLink?: string): Thunk =>
    (dispatch, getState) => {
        const { filters } = getState()
        // Use the latest draft config path if available (covers mobile modal case
        // where filterClick deferred the lastDataPathEncoded update)
        const latestPath = filters.filtersConfigurationResponse
            ? getRelativeURL(filters.filtersConfigurationResponse.encodedLink)
            : filters.lastDataPathEncoded

        try {
            // Sync lastDataPathEncoded with the latest draft state
            dispatch(setLastDataPathEncoded(latestPath))

            if (!filters.spaMode) {
                // Full page navigation (map view)
                globalThis.location.href = encodedLink ? decodeFromBase64(encodedLink) : decodeFromBase64(latestPath)
            }
            // Commit draft → committed
            dispatch(commitFiltersConfiguration())
        } catch (error) {
            console.error({ message: 'Error occurred in filter submit', error })
        }
    }

export const smartSearchSubmit =
    (text: string, user: RequestUserConfig): Thunk<Promise<SmartSearchResult>> =>
    async dispatch => {
        dispatch(setLoading(true))
        try {
            const response = await searchClient.smartSearch({
                user,
                filter: getRelativeURL(globalThis.location.href),
                text,
            })

            const result = response.data

            if (!result?.matched || !result.configuration) {
                return { matched: false, objectCount: 0 }
            }

            const config = result.configuration

            dispatch(setFiltersConfiguration({ config, shouldCommit: true }))
            dispatch(clearSearchRequestId())
            dispatch(setLastDataPathEncoded(getRelativeURL(config.encodedLink)))

            return { matched: true, objectCount: config.objectCount }
        } catch (error) {
            // Deliberately not setFiltersError: the filters are still valid, only the query failed,
            // and that error state would swap the whole sidebar for an ErrorCard.
            console.error({ message: 'Error occurred in smart search API', error })

            return { matched: false, objectCount: 0 }
        } finally {
            dispatch(setLoading(false))
        }
    }

/**
 * Fetches the initial filters configuration and initializes the Redux state.
 * Replaces the old useFiltersConfiguration hook + FiltersInitializer flow.
 */
export const fetchInitialFilters =
    (user: RequestUserConfig, regionNodeId: number, spaMode: boolean): Thunk<Promise<void>> =>
    async dispatch => {
        try {
            const filter = getRelativeURL(globalThis.location.href)
            const config = await _fetchFiltersConfiguration(user, filter)
            dispatch(initializeFilters({ config, regionNodeId, spaMode }))
        } catch (error) {
            console.error({ message: 'Error occurred in initial filters fetch', error })
            dispatch(setFiltersError(error as Error))
        }
    }
